import React, { useEffect, useState } from 'react';
import { Field, Input, Stack, Text } from '@chakra-ui/react';
import EmailVerificationPanel from './EmailVerificationPanel';
import { VERIFICATION_PURPOSE } from '../../utils/verification';
import AuthDialog from '../ui/AuthDialog';
import { FormActions, PrimaryButton, SecondaryButton } from '../ui/FormActions';

function RegisterModal({ open, onClose, onSubmit, onSwitch, onVerified, lang }) {
  const [step, setStep] = useState('register');
  const [registeredEmail, setRegisteredEmail] = useState('');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [emailError, setEmailError] = useState('');
  const [submitError, setSubmitError] = useState('');

  const isEn = lang === 'en';

  useEffect(() => {
    if (open) {
      setStep('register');
      setRegisteredEmail('');
      setName('');
      setEmail('');
      setPassword('');
      setConfirmPassword('');
      setPasswordError('');
      setEmailError('');
      setSubmitError('');
    }
  }, [open]);

  const canSubmit =
    password.length >= 8 && confirmPassword.length >= 8 && password === confirmPassword;

  const handleVerified = () => {
    if (typeof onVerified === 'function') {
      onVerified(registeredEmail);
    }
    onClose();
  };

  if (step === 'verify' && registeredEmail) {
    return (
      <AuthDialog open={open} onClose={onClose} title={isEn ? 'Verify your email' : '验证邮箱'}>
        <EmailVerificationPanel
          email={registeredEmail}
          purpose={VERIFICATION_PURPOSE.REGISTER_VERIFY}
          lang={lang}
          onSuccess={handleVerified}
        />
        <FormActions>
          <SecondaryButton onClick={onClose}>{isEn ? 'Skip for now' : '稍后验证'}</SecondaryButton>
        </FormActions>
      </AuthDialog>
    );
  }

  return (
    <AuthDialog
      open={open}
      onClose={onClose}
      title={isEn ? 'Sign up' : '注册'}
      description={
        isEn ? 'Create a new account to start using AEVDIR.' : '创建一个新账户，开始使用 AEVDIR。'
      }
    >
      {submitError ? (
        <Text fontSize="sm" color="red.500" mb={3}>
          {submitError}
        </Text>
      ) : null}
      <form
        onSubmit={async (e) => {
          e.preventDefault();
          setSubmitError('');
          if (password !== confirmPassword) {
            setPasswordError(isEn ? 'Passwords do not match.' : '两次输入的密码不一致。');
            return;
          }
          if (password.length < 8) {
            return;
          }
          const trimmedEmail = email.trim();
          if (trimmedEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedEmail)) {
            setEmailError(isEn ? 'Please enter a valid email address.' : '请输入有效的邮箱地址。');
            return;
          }
          setPasswordError('');
          setEmailError('');
          try {
            const payload = { name: name.trim(), password };
            if (trimmedEmail) {
              payload.email = trimmedEmail;
            }
            await onSubmit(payload);
            if (trimmedEmail) {
              setRegisteredEmail(trimmedEmail);
              setStep('verify');
            } else {
              onClose();
            }
          } catch (err) {
            setSubmitError(err?.message || (isEn ? 'Sign up failed' : '注册失败'));
          }
        }}
      >
        <Stack gap={3}>
          <Field.Root required>
            <Input
              value={name}
              onChange={(e) => {
                setName(e.target.value);
                if (submitError) setSubmitError('');
              }}
              placeholder={isEn ? 'Username' : '用户名'}
              borderRadius="lg"
              bg={{ base: 'white', _dark: 'gray.900' }}
            />
          </Field.Root>
          <Field.Root invalid={Boolean(emailError)}>
            <Input
              type="email"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                if (emailError) setEmailError('');
                if (submitError) setSubmitError('');
              }}
              placeholder={isEn ? 'Email (optional)' : '邮箱（可选）'}
              borderRadius="lg"
              bg={{ base: 'white', _dark: 'gray.900' }}
            />
            {emailError ? <Field.ErrorText>{emailError}</Field.ErrorText> : null}
          </Field.Root>
          <Field.Root required>
            <Input
              type="password"
              value={password}
              onChange={(e) => {
                setPassword(e.target.value);
                if (passwordError) setPasswordError('');
                if (submitError) setSubmitError('');
              }}
              placeholder={isEn ? 'Password (at least 8 characters)' : '密码（至少8位）'}
              minLength={8}
              borderRadius="lg"
              bg={{ base: 'white', _dark: 'gray.900' }}
            />
          </Field.Root>
          <Field.Root required invalid={Boolean(passwordError)}>
            <Input
              type="password"
              value={confirmPassword}
              onChange={(e) => {
                setConfirmPassword(e.target.value);
                if (passwordError) setPasswordError('');
                if (submitError) setSubmitError('');
              }}
              placeholder={isEn ? 'Confirm password' : '确认密码'}
              minLength={8}
              borderRadius="lg"
              bg={{ base: 'white', _dark: 'gray.900' }}
            />
            {passwordError ? <Field.ErrorText>{passwordError}</Field.ErrorText> : null}
          </Field.Root>
        </Stack>
        <FormActions>
          <PrimaryButton disabled={!canSubmit}>{isEn ? 'Sign up' : '注册'}</PrimaryButton>
          <SecondaryButton onClick={onSwitch}>{isEn ? 'Go to log in' : '去登录'}</SecondaryButton>
          <SecondaryButton onClick={onClose}>{isEn ? 'Close' : '关闭'}</SecondaryButton>
        </FormActions>
      </form>
    </AuthDialog>
  );
}

export default RegisterModal;
